import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getEnvEndpoints } from "../../utils/functions";

/**
 * Per-publication rubric editor.
 */
const PublicationFavouritesEditor = ({
  municipalityId,
  lang,
  translate,
  createNotification,
}) => {
  const env = getEnvEndpoints();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activePublicationId, setActivePublicationId] = useState(null);
  // Local edit buffer: collectionId -> Set of enabled rubric ids
  const [editedRubrics, setEditedRubrics] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchPublications = useCallback(async () => {
    if (!municipalityId) {
      setData(null);
      return;
    }
    const token = localStorage.getItem("StadGemeenteToken");
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/publications/${municipalityId}/?lang=${lang}`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setData(response.data);

      // Pre-populate the edit buffer with the user's current state.
      const buffer = {};
      (response.data.publications || []).forEach((pub) => {
        const enabled = new Set();
        (pub.categories || []).forEach((cat) => {
          (cat.rubrics || []).forEach((r) => {
            if (r.user_enabled) enabled.add(r.id);
          });
        });
        buffer[pub.id] = enabled;
      });
      setEditedRubrics(buffer);

      // Default the active tab to the first publication.
      if (response.data.publications && response.data.publications.length > 0) {
        setActivePublicationId(response.data.publications[0].id);
      } else {
        setActivePublicationId(null);
      }
    } catch (err) {
      const detail = err?.response?.data?.status;
      if (err?.response?.status === 403) {
        setError(translate
          ? translate("must_follow_municipality") || "U volgt deze gemeente nog niet."
          : "U volgt deze gemeente nog niet.");
      } else {
        setError(detail || (translate ? translate("publications_load_error") || "Kon publicaties niet laden." : "Kon publicaties niet laden."));
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [municipalityId, lang, env.REACT_APP_BASE_MEDIA_URL, translate]);

  useEffect(() => {
    fetchPublications();
  }, [fetchPublications]);

  const toggleRubric = (publicationId, rubricId) => {
    setEditedRubrics((prev) => {
      const next = { ...prev };
      const current = new Set(next[publicationId] || []);
      if (current.has(rubricId)) {
        current.delete(rubricId);
      } else {
        current.add(rubricId);
      }
      next[publicationId] = current;
      return next;
    });
  };

  const handleSave = async (publication) => {
    const token = localStorage.getItem("StadGemeenteToken");
    if (!token || !municipalityId || !publication) return;
    const rubricIds = Array.from(editedRubrics[publication.id] || []);
    setSaving(true);
    try {
      const response = await axios.post(
        `${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/publications/${municipalityId}/${publication.id}/preferences/?lang=${lang}`,
        { rubric_ids: rubricIds },
        { headers: { Authorization: `Token ${token}` } }
      );
      setData(response.data);

      // Refresh the edit buffer from the server response.
      const buffer = {};
      (response.data.publications || []).forEach((pub) => {
        const enabled = new Set();
        (pub.categories || []).forEach((cat) => {
          (cat.rubrics || []).forEach((r) => {
            if (r.user_enabled) enabled.add(r.id);
          });
        });
        buffer[pub.id] = enabled;
      });
      setEditedRubrics(buffer);

      if (createNotification) {
        createNotification("success", translate ? translate("favourites_saved") || "Favorieten opgeslagen" : "Favorieten opgeslagen");
      }
    } catch (err) {
      if (createNotification) {
        createNotification("error", translate ? translate("favourites_save_error") || "Opslaan mislukt" : "Opslaan mislukt");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!municipalityId) {
    return (
      <div className="publication-editor publication-editor--empty">
        {translate
          ? translate("select_a_municipality") || "Selecteer eerst een gemeente."
          : "Selecteer eerst een gemeente."}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="publication-editor publication-editor--loading">
        {translate ? translate("loading") || "Laden..." : "Laden..."}
      </div>
    );
  }

  if (error) {
    return (
      <div className="publication-editor publication-editor--error">
        {error}
      </div>
    );
  }

  if (!data || !data.publications || data.publications.length === 0) {
    return (
      <div className="publication-editor publication-editor--empty">
        <h2>
          {translate
            ? translate("favourites_for_selected_municipality") ||
              "Favorieten voor geselecteerde gemeente"
            : "Favorieten voor geselecteerde gemeente"}
        </h2>
        <p>
          {translate
            ? translate("no_publications_for_municipality") ||
              "Voor deze gemeente zijn nog geen publicaties beschikbaar."
            : "Voor deze gemeente zijn nog geen publicaties beschikbaar."}
        </p>
      </div>
    );
  }

  const activePublication =
    data.publications.find((p) => p.id === activePublicationId) ||
    data.publications[0];

  return (
    <div className="publication-editor">
      <h2>
        {translate
          ? translate("favourites_for_selected_municipality") ||
            "Favorieten voor geselecteerde gemeente"
          : "Favorieten voor geselecteerde gemeente"}
        : <span className="publication-editor__muni">{data.municipality?.name}</span>
      </h2>

      {/* Publication tabs */}
      <div className="publication-editor__tabs">
        {data.publications.map((pub) => (
          <button
            type="button"
            key={pub.id}
            className={
              "publication-tab" +
              (pub.id === activePublication.id ? " publication-tab--active" : "")
            }
            onClick={() => setActivePublicationId(pub.id)}
          >
            {pub.name || pub.internal_name}
          </button>
        ))}
      </div>

      {/* Active publication content */}
      <div className="publication-editor__body">
        <PublicationBody
          publication={activePublication}
          enabledRubricIds={editedRubrics[activePublication.id] || new Set()}
          onToggle={(rubricId) => toggleRubric(activePublication.id, rubricId)}
          translate={translate}
        />

        <div className="publication-editor__actions">
          <button
            type="button"
            className="publication-editor__save-btn"
            onClick={() => handleSave(activePublication)}
            disabled={saving}
          >
            {saving
              ? translate
                ? translate("saving") || "Opslaan..."
                : "Opslaan..."
              : translate
              ? translate("save_favourites") || "Favorieten opslaan"
              : "Favorieten opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the categories + rubrics for one publication.
 *
 * Layout: each category is a vertical column. Multiple categories sit side-by-side
 * in a CSS grid. Column count caps at 3 — beyond that, categories wrap onto new
 * rows of 3. The grid template is set inline (not in CSS) because the column
 * count depends on the data:
 *   1 category  → 1 column (full width)
 *   2 categories → 2 columns (50/50)
 *   3 categories → 3 columns (1/3 each)
 *   4+ categories → 3 columns, wraps to a new row of 3
 *
 * The category heading is hidden when there's only one category (cleaner UX
 * for publications that don't need grouping).
 */
const PublicationBody = ({ publication, enabledRubricIds, onToggle, translate }) => {
  if (!publication.categories || publication.categories.length === 0) {
    return (
      <div className="publication-body publication-body--empty">
        {translate
          ? translate("no_categories_yet") ||
            "Voor deze publicatie zijn nog geen rubrieken beschikbaar."
          : "Voor deze publicatie zijn nog geen rubrieken beschikbaar."}
      </div>
    );
  }

  const categoryCount = publication.categories.length;
  const columnCount = Math.min(categoryCount, 3);

  return (
    <div
      className="publication-body"
      style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
    >
      {publication.categories.map((category) => (
        <div className="rubric-category" key={category.id}>
          <h3 className="rubric-category__heading">{category.name}</h3>
          <div className="rubric-category__list">
            {(category.rubrics || []).map((rubric) => {
              const isChecked = enabledRubricIds.has(rubric.id);
              return (
                <label className="rubric-checkbox" key={rubric.id}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle(rubric.id)}
                  />
                  <span className="rubric-checkbox__label">{rubric.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PublicationFavouritesEditor;
